import * as yup from 'yup';
// import theme from 'app/theme';
import ProgressButton from 'app/shared-components/ui/ProgressButton';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PaidolUser, Role } from 'API';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { isErrorLike } from 'util/serializeError';
import { yupResolver } from '@hookform/resolvers/yup';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, Button, InputAdornment, TextField } from '@mui/material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { getAllUsersNoLimitDebounced, selectUsersSlice } from 'app/pages/store/usersSlice';
import { useSnackbar } from 'notistack';
import { sentryMask } from 'sentryReplayPrivacy';
import {
  selectSpecificCardSlice,
  resetSpecificCard,
  triggerGroupUpdate,
} from 'app/pages/store/CardDataSlice/cardDataSlice';
import {
  assignPaymentCard,
  getCard,
  getHighnotePaymentCard,
} from 'app/pages/store/CardDataSlice/cardsThunks';

/**
 * Form Validation Schema
 */
const schema = yup.object({
  highnotePaymentCardId: yup.string().required(),
  email: yup.string().required('You must enter a email').email('You must enter a valid email').nullable(),
  user: yup.object(),
});

type FormValues = yup.InferType<typeof schema>;

interface AssignCardProps {
  selectedCardId?: string;
  onClose?: () => void;
  onSuccess?: (paymentCardId: string) => void;
}

function AssignCardForm({ selectedCardId, onClose, onSuccess }: AssignCardProps) {
  const dispatch = useAppDispatch();
  const { selectedPaidol, selectedPaidolId: selectedCompany } = useAppSelector(selectUserCompanies);
  const { paymentCardDetails: selectedCardDetails, cardUsers } = useAppSelector(selectSpecificCardSlice);
  const selectedCard = selectedCardDetails?.highnotePaymentCard;
  const [selectedUser, setSelectedUser] = useState<PaidolUser | undefined>(undefined);
  const { allUsers: users } = useAppSelector(selectUsersSlice);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // SEE-946: Secondary admins are never permitted to hold a card per the
  // matrix. Filter strictly by the stored role rather than by the AppConfig
  // flag — `roles === SECONDARY_ADMINISTRATOR` is a server-side fact about
  // that user and shouldn't depend on whether the flag is currently
  // loaded/on for the *caller's* paidol context. Non-flag paidols have no
  // users carrying that role in DDB, so the filter is a no-op there (zero
  // regression). Earlier code gated this on `additionalAdminRolesEnabled`
  // which could be `undefined` during the feature-flag bootstrap and let
  // Secondary records leak into the dropdown.
  const assignableUsers = useMemo(
    () => users.filter((u) => u?.roles !== Role.SECONDARY_ADMINISTRATOR),
    [users]
  );

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      user: undefined,
      highnotePaymentCardId: '',
    },
    resolver: yupResolver(schema),
  });
  const { control, formState, getValues, setValue, setError } = methods;
  const { errors, isValid } = formState;
  const [isLoading, setIsLoading] = useState(false);

  const fetchAllUsers = useCallback(
    (email?: string) => {
      if (selectedPaidol?.id) {
        dispatch(getAllUsersNoLimitDebounced({ selectedCompany: selectedPaidol.id, email }));
      }
    },
    [dispatch, selectedPaidol]
  );

  useEffect(() => {
    fetchAllUsers();
    if (selectedCardId && selectedCompany) {
      dispatch(
        getHighnotePaymentCard({
          paymentCardId: selectedCardId,
          paidolId: selectedCompany,
          yearAndMonth: format(new Date(), 'yyyy-MM'),
        })
      );
    }

    return () => {
      dispatch(resetSpecificCard());
    };
  }, [dispatch, fetchAllUsers, selectedCardId, selectedCompany]);

  useEffect(() => {
    if (cardUsers?.[0] !== undefined) {
      setSelectedUser(cardUsers[0].paidolUser ?? undefined);
    }

    return () => {
      setSelectedUser(undefined);
    };
  }, [cardUsers]);

  useDeepCompareEffect(() => {
    if (selectedCard) {
      setValue('highnotePaymentCardId', selectedCard.paymentCardId, {
        shouldDirty: false,
      });
    }
  }, [selectedCard, setValue]);

  const assignCard = useCallback(() => {
    const { email, user, highnotePaymentCardId } = getValues();

    // SEE-946: Secondary admins are never permitted to hold a card per the
    // matrix — guard the assignment regardless of who's logged in (Primary
    // or Secondary, doesn't matter — neither can assign a card to a
    // Secondary admin user). Two paths need to be blocked:
    //   1. The caller picked a Secondary user from the autocomplete →
    //      selectedUser carries the role directly.
    //   2. The caller typed a Secondary admin's email manually → look up
    //      the entered email in the loaded users list and check there.
    // The backend assign_card.py also returns 403 with the same message so
    // the snackbar copy stays in sync regardless of which layer catches it.
    // Same role-based (not flag-based) approach as `assignableUsers` above.
    const typedEmailMatch = email
      ? users.find((u) => u?.email && u.email.toLowerCase() === email.toLowerCase())
      : undefined;
    const targetRole = selectedUser?.roles ?? typedEmailMatch?.roles;
    if (targetRole === Role.SECONDARY_ADMINISTRATOR) {
      enqueueSnackbar(t('assignCard.secondaryAdminBlocked'), { variant: 'warning' });
      setError('email', { type: 'manual', message: t('assignCard.secondaryAdminBlocked') });
      return;
    }

    setIsLoading(true);
    dispatch(
      assignPaymentCard({
        input: {
          email: email.toLowerCase(),
          employee_id: user?.id,
          paidol_id: selectedCompany,
        },

        highnotePaymentCardId,
      })
    ).then((res) => {
      setIsLoading(false);
      if (isErrorLike(res?.payload)) {
        setError('email', { type: 'manual', message: res?.payload?.message });
      } else {
        // Refetch card data - getCard automatically updates both list and specific card state
        if (selectedCompany) {
          dispatch(
            getCard({
              filter: {
                and: [{ or: [{ paymentCardId: { eq: highnotePaymentCardId } }] }],
                paidolId: { eq: selectedCompany },
              },
            })
          );
        }

        if (onSuccess) {
          onSuccess(highnotePaymentCardId);
          // Don't reset groups slice - it clears group cards that are being displayed
          dispatch(triggerGroupUpdate());
        }

        if (onClose) {
          onClose();
        }
      }
    });
  }, [
    dispatch,
    enqueueSnackbar,
    getValues,
    onClose,
    onSuccess,
    selectedCompany,
    selectedUser,
    setError,
    t,
    users,
  ]);

  return (
    <>
      <FormProvider {...methods}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', py: 2 }}>
            <Box sx={{ flexGrow: 1, px: 2, pb: 2 }}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    freeSolo
                    ListboxProps={sentryMask}
                    slotProps={{ popper: sentryMask }}
                    options={assignableUsers}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.email || '')}
                    value={selectedUser || field.value || ''}
                    onInputChange={(event, newInputValue) => {
                      field.onChange(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'object' && newValue !== null) {
                        setSelectedUser(newValue as PaidolUser);
                        field.onChange(newValue.email);
                      } else {
                        setSelectedUser(undefined);
                        field.onChange(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        type="text"
                        error={!!fieldState.error || !!errors.email}
                        helperText={fieldState.error?.message || errors?.email?.message}
                        label={t('cardholdersEmail')}
                        variant="outlined"
                        required
                        fullWidth
                        onChange={(e) => {
                          fetchAllUsers(e.target.value);
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccountCircleOutlinedIcon />
                            </InputAdornment>
                          ),
                          inputProps: {
                            ...params.inputProps,
                            style: { paddingLeft: '8px' },
                            ...sentryMask,
                          },
                        }}
                      />
                    )}
                  />
                )}
              />
            </Box>
            <Box sx={{ flexGrow: 1, px: 2 }}>
              <Controller
                name="highnotePaymentCardId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    error={!!errors.highnotePaymentCardId}
                    helperText={errors?.highnotePaymentCardId?.message}
                    label="Payment Card ID"
                    id="highnotePaymentCardId"
                    variant="outlined"
                    disabled={!!selectedCardId}
                    required
                    fullWidth
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={{ p: 2, mt: 4, display: 'flex', justifyContent: 'right' }}>
            <Button sx={{ mr: 2 }} onClick={onClose}>
              Cancel
            </Button>
            <ProgressButton
              loading={isLoading}
              disabled={isLoading || !isValid || !selectedUser}
              onClick={assignCard}
            >
              Assign Card
            </ProgressButton>
          </Box>
        </Box>
      </FormProvider>
    </>
  );
}

export default AssignCardForm;
